import React, { useEffect, useState } from "react";
import axios from "axios";

/**
 * TEMPLE STUDIO — private image-generation tool
 *
 * URL: /temple-studio (noindex)
 *
 * Workflow:
 *  1. Choose face reference (illustrated banner or upload a selfie)
 *  2. Pick a style preset (Bougie Boho Sunset / Indoor / Garden)
 *  3. Describe the scene
 *  4. (Optional) Add a quote that gets rendered INTO the image
 *  5. Pick aspect ratio
 *  6. Click Generate → 30-90 sec → finished image ready to download
 *
 * Initial gallery loads the 5 curated samples from samples-manifest.json
 */

const BRAND = {
  deep: "#042F2E",
  teal: "#068fa2",
  gold: "#d4af6a",
  cream: "#efeae7",
  mint: "#5EEAD4",
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STYLE_LABELS = {
  bougie_boho_sunset: {
    name: "Bougie Boho · Sunset",
    description: "Curated patio at golden hour. Brass lanterns, real crystals, peonies, pampas grass.",
  },
  bougie_boho_indoor: {
    name: "Bougie Boho · Indoor",
    description: "Curated sanctuary. Window light, white linens, candles, marble, polished crystals.",
  },
  bougie_boho_garden: {
    name: "Bougie Boho · Garden",
    description: "Walled Mediterranean garden. Climbing roses, olive branches, iron lanterns.",
  },
};

const ASPECTS = [
  { id: "portrait", label: "Portrait (IG Feed)", note: "1080×1350 · 4:5" },
  { id: "square", label: "Square", note: "1080×1080 · 1:1" },
  { id: "story", label: "Story / Reel", note: "1080×1920 · 9:16" },
];

const QUOTE_SUGGESTIONS = [
  "11:11 is the universe winking back.",
  "I became myself the moment I stopped waiting for permission.",
  "Real tools. Real women. The messy middle.",
  "Stop saying I have to. Start LIVING I GET TO.",
  "The Divine team picked these numbers. Not me.",
  "Bougie Boho. Real. Authentic. Transparent.",
  "Welcome home, Lovely.",
  "Brought down to the studs. Built back better.",
];

export default function TempleStudioPage() {
  const [faceRef, setFaceRef] = useState("illustrated");
  const [uploadedFaceB64, setUploadedFaceB64] = useState(null);
  const [uploadedPreview, setUploadedPreview] = useState(null);
  const [styleId, setStyleId] = useState("bougie_boho_sunset");
  const [scenePrompt, setScenePrompt] = useState("");
  const [quoteText, setQuoteText] = useState("");
  const [aspect, setAspect] = useState("portrait");
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [samples, setSamples] = useState([]);

  useEffect(() => {
    document.title = "Temple Studio · Private";

    // Make this page noindex
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");

    // Load the 5 curated samples (manifest is written by seed script)
    axios
      .get("/studio-assets/samples-manifest.json")
      .then((r) => setSamples(r.data?.samples || []))
      .catch(() => setSamples([]));

    return () => {
      const stale = document.querySelector('meta[name="robots"]');
      if (stale) stale.remove();
    };
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setUploadedPreview(dataUrl);
      // Strip data URL prefix to get raw base64
      const b64 = dataUrl.split(",")[1];
      setUploadedFaceB64(b64);
      setFaceRef("uploaded");
    };
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (!scenePrompt.trim()) {
      setErrorMsg("Tell me the scene first, love.");
      return;
    }
    setGenerating(true);
    setErrorMsg("");
    try {
      const { data } = await axios.post(
        `${API_URL}/api/temple-studio/generate`,
        {
          scene_prompt: scenePrompt.trim(),
          quote_text: quoteText.trim() || null,
          style_preset: styleId,
          face_reference: faceRef,
          uploaded_face_base64: faceRef === "uploaded" ? uploadedFaceB64 : null,
          aspect,
        },
        { timeout: 120000 }
      );
      setGenerated((prev) => [
        { ...data, quote: quoteText.trim(), scene: scenePrompt.trim(), createdAt: Date.now() },
        ...prev,
      ]);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.detail ||
          err?.message ||
          "Something hiccupped. Try a slightly different prompt."
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      className="min-h-screen px-6 py-12 md:px-12 md:py-16"
      style={{ background: BRAND.cream }}
      data-testid="temple-studio-page"
    >
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="mb-12 text-center">
          <p
            className="font-sans text-xs tracking-[0.4em] uppercase mb-3"
            style={{ color: BRAND.gold }}
          >
            Private · For Temple's eyes only
          </p>
          <h1
            className="font-serif tracking-tight leading-[1.05] mb-3"
            style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", color: BRAND.deep }}
            data-testid="studio-headline"
          >
            Temple{" "}
            <span style={{ color: BRAND.teal, fontStyle: "italic" }}>Studio</span>
          </h1>
          <p
            className="font-serif italic text-lg max-w-2xl mx-auto"
            style={{ color: `${BRAND.deep}cc` }}
          >
            Type a scene. Type a quote. Click Generate. Bougie Boho — every time. ✦
          </p>
        </div>

        {/* CURATED SAMPLES GALLERY (the 5 launch posts) */}
        {samples.length > 0 && (
          <div className="mb-16" data-testid="studio-samples-section">
            <h2
              className="font-serif tracking-tight mb-2"
              style={{ fontSize: "1.8rem", color: BRAND.deep }}
            >
              Your launch-week posts —{" "}
              <span className="italic" style={{ color: BRAND.teal }}>
                ready to go
              </span>
            </h2>
            <p className="font-sans text-sm mb-6" style={{ color: `${BRAND.deep}88` }}>
              I made these for you tonight. Right-click any image → Save As, then post.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {samples.map((s, i) => (
                <a
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl overflow-hidden transition-transform hover:scale-[1.02]"
                  style={{
                    boxShadow: `0 14px 36px ${BRAND.deep}1f, 0 0 0 1px ${BRAND.gold}33`,
                    background: "white",
                  }}
                  data-testid={`sample-${i}`}
                >
                  <img
                    src={s.url}
                    alt={s.label}
                    loading="lazy"
                    className="w-full h-auto block"
                  />
                  <div
                    className="px-4 py-3"
                    style={{
                      background: BRAND.cream,
                      borderTop: `1px solid ${BRAND.gold}22`,
                    }}
                  >
                    <p
                      className="font-sans text-[10px] tracking-[0.3em] uppercase font-semibold mb-1"
                      style={{ color: BRAND.teal }}
                    >
                      {s.label}
                    </p>
                    <p
                      className="font-serif italic text-sm leading-snug"
                      style={{ color: BRAND.deep }}
                    >
                      "{s.quote}"
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* GENERATOR */}
        <div
          className="grid lg:grid-cols-5 gap-8 p-6 md:p-10 rounded-2xl"
          style={{
            background: "white",
            boxShadow: `0 24px 60px ${BRAND.deep}14`,
          }}
        >
          {/* LEFT: Form */}
          <div className="lg:col-span-2" data-testid="studio-form-column">
            <h2
              className="font-serif tracking-tight mb-6"
              style={{ fontSize: "1.6rem", color: BRAND.deep }}
            >
              Make a new one
            </h2>

            {/* 1. Face reference */}
            <div className="mb-6">
              <label
                className="block font-sans text-[10px] tracking-[0.3em] uppercase font-semibold mb-3"
                style={{ color: BRAND.teal }}
              >
                1 · Your face
              </label>
              <div className="flex gap-3 items-center mb-3">
                <button
                  type="button"
                  onClick={() => setFaceRef("illustrated")}
                  className="px-4 py-2 rounded-full font-sans text-xs font-semibold tracking-wide transition-all"
                  style={{
                    background:
                      faceRef === "illustrated" ? BRAND.deep : "transparent",
                    color:
                      faceRef === "illustrated" ? BRAND.cream : BRAND.deep,
                    border: `1px solid ${BRAND.deep}`,
                  }}
                  data-testid="face-illustrated"
                >
                  Illustrated banner
                </button>
                <label
                  className="px-4 py-2 rounded-full font-sans text-xs font-semibold tracking-wide transition-all cursor-pointer"
                  style={{
                    background: faceRef === "uploaded" ? BRAND.deep : "transparent",
                    color: faceRef === "uploaded" ? BRAND.cream : BRAND.deep,
                    border: `1px solid ${BRAND.deep}`,
                  }}
                  data-testid="face-upload-label"
                >
                  Upload a selfie
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="face-upload-input"
                  />
                </label>
              </div>
              {faceRef === "uploaded" && uploadedPreview && (
                <img
                  src={uploadedPreview}
                  alt="Your selfie"
                  className="w-24 h-24 object-cover rounded-full"
                  style={{ boxShadow: `0 0 0 2px ${BRAND.gold}` }}
                />
              )}
              {faceRef === "illustrated" && (
                <img
                  src="/studio-assets/sources/temple-banner.png"
                  alt="Illustrated banner"
                  className="w-24 h-24 object-cover rounded-full"
                  style={{ boxShadow: `0 0 0 2px ${BRAND.gold}` }}
                />
              )}
            </div>

            {/* 2. Style */}
            <div className="mb-6">
              <label
                className="block font-sans text-[10px] tracking-[0.3em] uppercase font-semibold mb-3"
                style={{ color: BRAND.teal }}
              >
                2 · Style
              </label>
              <div className="space-y-2">
                {Object.entries(STYLE_LABELS).map(([id, info]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setStyleId(id)}
                    className="w-full text-left px-4 py-3 rounded-lg transition-all"
                    style={{
                      background: styleId === id ? `${BRAND.teal}15` : "transparent",
                      border:
                        styleId === id
                          ? `1px solid ${BRAND.teal}`
                          : `1px solid ${BRAND.deep}22`,
                    }}
                    data-testid={`style-${id}`}
                  >
                    <div
                      className="font-sans text-sm font-semibold mb-0.5"
                      style={{ color: BRAND.deep }}
                    >
                      {info.name}
                    </div>
                    <div
                      className="font-serif italic text-xs"
                      style={{ color: `${BRAND.deep}aa` }}
                    >
                      {info.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* 3. Scene */}
            <div className="mb-6">
              <label
                className="block font-sans text-[10px] tracking-[0.3em] uppercase font-semibold mb-3"
                style={{ color: BRAND.teal }}
              >
                3 · The scene
              </label>
              <textarea
                value={scenePrompt}
                onChange={(e) => setScenePrompt(e.target.value)}
                placeholder="Temple seated on a linen cushion at golden hour, holding a clear quartz crystal, white peonies beside her…"
                rows={5}
                className="w-full px-4 py-3 rounded-lg font-serif text-sm leading-relaxed outline-none transition-all"
                style={{
                  background: BRAND.cream,
                  color: BRAND.deep,
                  border: `1px solid ${BRAND.deep}22`,
                }}
                data-testid="scene-input"
              />
            </div>

            {/* 4. Quote */}
            <div className="mb-6">
              <label
                className="block font-sans text-[10px] tracking-[0.3em] uppercase font-semibold mb-3"
                style={{ color: BRAND.teal }}
              >
                4 · Quote on the image (optional)
              </label>
              <input
                type="text"
                value={quoteText}
                onChange={(e) => setQuoteText(e.target.value)}
                placeholder="Leave blank for no text overlay"
                className="w-full px-4 py-3 rounded-lg font-serif text-sm outline-none mb-3"
                style={{
                  background: BRAND.cream,
                  color: BRAND.deep,
                  border: `1px solid ${BRAND.deep}22`,
                }}
                data-testid="quote-input"
              />
              <div className="flex flex-wrap gap-2">
                {QUOTE_SUGGESTIONS.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setQuoteText(q)}
                    className="font-serif italic text-xs px-3 py-1.5 rounded-full transition-all hover:scale-[1.03]"
                    style={{
                      background: `${BRAND.teal}11`,
                      color: BRAND.teal,
                      border: `1px solid ${BRAND.teal}33`,
                    }}
                    data-testid="quote-suggestion"
                  >
                    "{q.length > 40 ? q.slice(0, 40) + "…" : q}"
                  </button>
                ))}
              </div>
            </div>

            {/* 5. Aspect */}
            <div className="mb-6">
              <label
                className="block font-sans text-[10px] tracking-[0.3em] uppercase font-semibold mb-3"
                style={{ color: BRAND.teal }}
              >
                5 · Size
              </label>
              <div className="flex flex-wrap gap-2">
                {ASPECTS.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setAspect(a.id)}
                    className="px-4 py-2 rounded-full font-sans text-xs font-semibold tracking-wide transition-all"
                    style={{
                      background: aspect === a.id ? BRAND.deep : "transparent",
                      color: aspect === a.id ? BRAND.cream : BRAND.deep,
                      border: `1px solid ${BRAND.deep}`,
                    }}
                    data-testid={`aspect-${a.id}`}
                  >
                    {a.label}
                    <span
                      style={{
                        opacity: 0.7,
                        marginLeft: 6,
                        fontSize: "10px",
                      }}
                    >
                      {a.note}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Generate button */}
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating || !scenePrompt.trim()}
              className="w-full inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full font-sans font-semibold tracking-wide transition-all hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: BRAND.mint, color: BRAND.deep, fontSize: "15px" }}
              data-testid="generate-button"
            >
              {generating ? "Painting your scene…" : "✦ Generate"}
            </button>
            {generating && (
              <p
                className="font-serif italic text-xs mt-3 text-center"
                style={{ color: `${BRAND.deep}88` }}
              >
                30-90 sec. The Divine team takes its time.
              </p>
            )}
            {errorMsg && (
              <p
                className="font-sans text-xs mt-3 text-center"
                style={{ color: "#c43a3a" }}
                data-testid="generate-error"
              >
                {errorMsg}
              </p>
            )}
          </div>

          {/* RIGHT: Generated images */}
          <div className="lg:col-span-3" data-testid="studio-output-column">
            <h2
              className="font-serif tracking-tight mb-6"
              style={{ fontSize: "1.6rem", color: BRAND.deep }}
            >
              Your new images
            </h2>
            {generated.length === 0 && (
              <div
                className="p-10 rounded-xl text-center"
                style={{
                  background: BRAND.cream,
                  border: `1px dashed ${BRAND.deep}33`,
                }}
                data-testid="studio-empty-state"
              >
                <p
                  className="font-serif italic text-lg mb-2"
                  style={{ color: `${BRAND.deep}88` }}
                >
                  Nothing yet, Lovely. ✦
                </p>
                <p
                  className="font-sans text-sm"
                  style={{ color: `${BRAND.deep}66` }}
                >
                  Describe a scene, pick a vibe, hit Generate. New images appear here.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {generated.map((g, i) => (
                <a
                  key={g.file_name}
                  href={g.image_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="block rounded-xl overflow-hidden transition-transform hover:scale-[1.02]"
                  style={{
                    boxShadow: `0 14px 36px ${BRAND.deep}1f, 0 0 0 1px ${BRAND.gold}33`,
                    background: "white",
                  }}
                  data-testid={`generated-${i}`}
                >
                  <img src={g.image_url} alt="Generated" className="w-full h-auto block" />
                  {g.quote && (
                    <div
                      className="px-4 py-3"
                      style={{
                        background: BRAND.cream,
                        borderTop: `1px solid ${BRAND.gold}22`,
                      }}
                    >
                      <p
                        className="font-serif italic text-sm leading-snug"
                        style={{ color: BRAND.deep }}
                      >
                        "{g.quote}"
                      </p>
                      <p
                        className="font-sans text-[10px] tracking-wide mt-2"
                        style={{ color: `${BRAND.deep}66` }}
                      >
                        Right-click → Save As → post.
                      </p>
                    </div>
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>

        <a
          href="/"
          className="block text-center mt-12 font-sans text-sm tracking-wide underline-offset-4 hover:underline"
          style={{ color: `${BRAND.deep}88` }}
        >
          ← Back to mindbodyspirithygienetools.com
        </a>
      </div>
    </div>
  );
}
